import { useId, useState } from 'react';
import SheetDialog from '../SheetDialog';
import { AddButton, Field, SectionCard, TextArea, TextInput, WarningNote } from './fields';
import type { SectionProps } from './types';

export function HeroSection({ doc, onChange, disabled }: SectionProps) {
  return (
    <SectionCard title="Hero">
      <Field label="Client name">
        <TextInput
          value={doc.client_name}
          onChange={(e) => onChange((d) => ({ ...d, client_name: e.target.value }))}
          disabled={disabled}
        />
      </Field>
      <Field label="Subtitle" hint="The eyebrow line above the headline, e.g. 'Content Package Strategy'.">
        <TextInput
          value={doc.subtitle ?? ''}
          onChange={(e) => onChange((d) => ({ ...d, subtitle: e.target.value }))}
          disabled={disabled}
        />
      </Field>
      <Field label="Tagline" hint="Used for the page's link-preview description. Optional.">
        <TextInput
          value={doc.tagline ?? ''}
          onChange={(e) => onChange((d) => ({ ...d, tagline: e.target.value }))}
          disabled={disabled}
        />
      </Field>
      <Field label="Headline" hint="Wrap *words* in asterisks to highlight them.">
        <TextInput
          value={doc.hero.headline}
          onChange={(e) => onChange((d) => ({ ...d, hero: { ...d.hero, headline: e.target.value } }))}
          disabled={disabled}
        />
      </Field>
      <Field label="Subline">
        <TextArea
          rows={2}
          value={doc.hero.subline}
          onChange={(e) => onChange((d) => ({ ...d, hero: { ...d.hero, subline: e.target.value } }))}
          disabled={disabled}
        />
      </Field>
    </SectionCard>
  );
}

export function ChallengeSection({ doc, onChange, disabled }: SectionProps) {
  const points = doc.challenge.points;

  function updatePoint(index: number, value: string) {
    onChange((d) => ({
      ...d,
      challenge: { ...d.challenge, points: d.challenge.points.map((p, i) => (i === index ? value : p)) },
    }));
  }

  function addPoint() {
    if (points.length >= 4) return;
    onChange((d) => ({ ...d, challenge: { ...d.challenge, points: [...d.challenge.points, ''] } }));
  }

  function removePoint(index: number) {
    if (points.length <= 3) return;
    onChange((d) => ({
      ...d,
      challenge: { ...d.challenge, points: d.challenge.points.filter((_, i) => i !== index) },
    }));
  }

  return (
    <SectionCard title="The challenge">
      <Field label="Heading">
        <TextInput
          value={doc.challenge.heading}
          onChange={(e) => onChange((d) => ({ ...d, challenge: { ...d.challenge, heading: e.target.value } }))}
          disabled={disabled}
        />
      </Field>
      <Field label="Narrative" hint="2 to 4 paragraphs. Leave a blank line between paragraphs.">
        <TextArea
          rows={8}
          value={doc.challenge.narrative}
          onChange={(e) => onChange((d) => ({ ...d, challenge: { ...d.challenge, narrative: e.target.value } }))}
          disabled={disabled}
        />
      </Field>

      <div className="space-y-2.5">
        <span className="text-sm font-medium text-ink">
          Hinge points ({points.length} of 3–4)
        </span>
        <p className="text-xs text-ink-faint">
          Short, punchy named problems, 5 words or fewer, no descriptions. The Solution answers these one by one, in
          order.
        </p>
        {points.map((point, index) => (
          <div key={index} className="flex items-center gap-2">
            <TextInput
              value={point}
              maxLength={64}
              onChange={(e) => updatePoint(index, e.target.value)}
              disabled={disabled}
              className="flex-1"
            />
            <button
              type="button"
              onClick={() => removePoint(index)}
              disabled={disabled || points.length <= 3}
              className="text-xs font-medium text-ink-faint transition hover:text-error disabled:cursor-not-allowed disabled:opacity-40"
            >
              Remove
            </button>
          </div>
        ))}
        <AddButton onClick={addPoint} disabled={disabled || points.length >= 4}>
          + Add hinge point
        </AddButton>
      </div>
    </SectionCard>
  );
}

export function SolutionSection({ doc, onChange, disabled }: SectionProps) {
  const pointsId = useId();
  const responses = doc.solution.responses;
  const points = doc.challenge.points;

  function updateResponse(index: number, patch: Partial<(typeof responses)[number]>) {
    onChange((d) => ({
      ...d,
      solution: { ...d.solution, responses: d.solution.responses.map((r, i) => (i === index ? { ...r, ...patch } : r)) },
    }));
  }

  function addResponse() {
    if (responses.length >= 4) return;
    onChange((d) => ({
      ...d,
      solution: {
        ...d.solution,
        responses: [
          ...d.solution.responses,
          { challenge_label: points[d.solution.responses.length] ?? '', title: '', description: '' },
        ],
      },
    }));
  }

  function removeResponse(index: number) {
    if (responses.length <= 1) return;
    onChange((d) => ({
      ...d,
      solution: { ...d.solution, responses: d.solution.responses.filter((_, i) => i !== index) },
    }));
  }

  return (
    <SectionCard title="The solution">
      <Field label="Heading">
        <TextInput
          value={doc.solution.heading}
          onChange={(e) => onChange((d) => ({ ...d, solution: { ...d.solution, heading: e.target.value } }))}
          disabled={disabled}
        />
      </Field>
      <Field label="Narrative" hint="Optional short intro paragraph.">
        <TextArea
          rows={3}
          value={doc.solution.narrative ?? ''}
          onChange={(e) => onChange((d) => ({ ...d, solution: { ...d.solution, narrative: e.target.value } }))}
          disabled={disabled}
        />
      </Field>

      {responses.length !== points.length && (
        <WarningNote>
          {responses.length} response{responses.length === 1 ? '' : 's'} but {points.length} hinge point
          {points.length === 1 ? '' : 's'}. Each response should answer one hinge point, in order.
        </WarningNote>
      )}

      <datalist id={pointsId}>
        {points.map((point) => (
          <option key={point} value={point} />
        ))}
      </datalist>

      <div className="space-y-3">
        {responses.map((response, index) => (
          <div key={index} className="rounded-xl border border-line bg-paper p-4 space-y-3">
            <Field label="Answers hinge point" hint="Pick one from the Challenge, or type your own.">
              <TextInput
                list={pointsId}
                value={response.challenge_label}
                onChange={(e) => updateResponse(index, { challenge_label: e.target.value })}
                disabled={disabled}
              />
            </Field>
            <Field label="Title" hint="Names the deliverable or move. Never packages, prices, or numbers.">
              <TextInput
                value={response.title}
                onChange={(e) => updateResponse(index, { title: e.target.value })}
                disabled={disabled}
              />
            </Field>
            <Field label="Description">
              <TextArea
                rows={3}
                value={response.description}
                onChange={(e) => updateResponse(index, { description: e.target.value })}
                disabled={disabled}
              />
            </Field>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => removeResponse(index)}
                disabled={disabled || responses.length <= 1}
                className="text-xs font-medium text-ink-faint transition hover:text-error disabled:cursor-not-allowed disabled:opacity-40"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <AddButton onClick={addResponse} disabled={disabled || responses.length >= 4}>
          + Add response
        </AddButton>
      </div>
    </SectionCard>
  );
}

export function MobileStory(props: SectionProps) {
  const { doc, onChange, disabled } = props;
  const [open, setOpen] = useState(false);
  return <div className="lg:hidden">
    <SectionCard title="Story">
      <Field label="Hero headline"><TextInput value={doc.hero.headline} disabled={disabled} onChange={event => onChange(current => ({ ...current, hero: { ...current.hero, headline: event.target.value } }))} /></Field>
      <Field label="Challenge / situation"><TextArea rows={5} value={doc.challenge.narrative} disabled={disabled} onChange={event => onChange(current => ({ ...current, challenge: { ...current.challenge, narrative: event.target.value } }))} /></Field>
      <Field label="Solution"><TextArea rows={4} value={doc.solution.narrative ?? ''} disabled={disabled} onChange={event => onChange(current => ({ ...current, solution: { ...current.solution, narrative: event.target.value } }))} /></Field>
      <button className="pill-outline" onClick={() => setOpen(true)}>Edit all narrative fields</button>
    </SectionCard>
    <SheetDialog open={open} title="Narrative fields" onClose={() => setOpen(false)}>
      <div className="space-y-8"><HeroSection {...props} /><ChallengeSection {...props} /><SolutionSection {...props} />
        <button className="pill-outline" onClick={() => setOpen(false)}>Done</button></div>
    </SheetDialog>
  </div>;
}
